import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Dialog from 'cityads-v3/components/Dialog/Dialog'
import { useDialogActions } from 'cityads-v3/hooks/dialog'
import { usePreloadOffers } from 'cityads-v3/hooks/offers'
import { usePreloadWebmasterInfo } from 'cityads-v3/hooks/welcomeform'
import { RootState } from 'cityads-v3/types/reducer'

import { GEO_ID_WORLD, GEO_ID_CIS, GEO_ID_RUSSIA } from 'cityads-v3/reducers/offers'

import { useAdvertisingMarkingFormCount } from './AdvertisingMarkingForm.hooks'
import AdvertisingMarkingForm from './AdvertisingMarkingForm'

type AdvertisingMarkingDialogProps = {
  open   : boolean,
  offerId: number,
}

export default function AdvertisingMarkingDialog({ open, offerId }: AdvertisingMarkingDialogProps): JSX.Element {
  const { t } = useTranslation(['Publisher'])

  const isMobile = useSelector((state: RootState) => state.app.isMobile)
  const offersById = useSelector((state: RootState) => state.offers.offersById)
  const offer = offersById[offerId]
  const formCount = useAdvertisingMarkingFormCount()

  const offerIds = useMemo(() => offerId ? [offerId] : [], [offerId])

  usePreloadOffers(offerIds)
  usePreloadWebmasterInfo()

  const [internalOpen, openDialog, closeDialog] = useDialogActions(true)

  const handleClose = useCallback((evt: unknown, reason: string) => {
    if (reason === 'backdropClick') {
      return
    }

    closeDialog()
  }, [closeDialog])

  useEffect(() => {
    if (!open) {
      // Reset internal open flag on closing this dialog from parent component
      openDialog()
      // When parent component decides to open again, we'll show up again too
    }
  }, [open, openDialog])

  useEffect(() => {
    // Reset internal open flag on offer change
    openDialog()
  }, [offerId, openDialog])

  const isOfferForRussia = useMemo<boolean>(() => {
    return !!(offer?.regions.includes(GEO_ID_WORLD) || offer?.regions.includes(GEO_ID_CIS) || offer?.regions.includes(GEO_ID_RUSSIA))
  }, [offer?.regions])

  return (
    <Dialog
      open={isOfferForRussia && formCount === 0 && open && internalOpen}
      onRequestClose = {handleClose}
      title          = {t('advertisingMarking.title')}
      showLogo       = {false}
      fullScreen     = {isMobile}
    >
      <AdvertisingMarkingForm />
    </Dialog>
  )
}
